import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmt, hasPerm, fmtDate } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertCircle, Loader2, Search, MessageSquare, Send, HandCoins, Printer, AlertTriangle, Pencil, Filter, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import CollectFeeDialog from "./CollectFeeDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const GENDERS = ["Male", "Female", "Other"];
const CATEGORIES = ["General", "OBC", "SC", "ST", "Other"];
const FILTER_FIELDS = [
  { key: "class_name", label: "Class", type: "class" },
  { key: "section", label: "Section", type: "section" },
  { key: "gender", label: "Gender", type: "select", options: GENDERS },
  { key: "category", label: "Category", type: "select", options: CATEGORIES },
  { key: "house", label: "House", type: "text" },
  { key: "admission_type", label: "Admission Type", type: "select", options: ["New", "Old", "Left"] },
  { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
  { key: "tag", label: "Tag", type: "select", options: [] },
  { key: "without_fees", label: "Without Fees", type: "select", options: ["Yes"] },
];
const SORT_OPTIONS = [
  { value: "name", label: "Name" },
  { value: "admission_number", label: "Admission No" },
  { value: "roll_number", label: "Roll No" },
  { value: "section", label: "Section" },
  { value: "created_at", label: "Date Added" },
];

function PromiseCell({ s, onSaved }) {
  const [date, setDate] = useState(s.promise_date || "");
  const [amount, setAmount] = useState(s.promise_amount || "");
  const save = async () => {
    const changed = (date || "") !== (s.promise_date || "") || Number(amount || 0) !== Number(s.promise_amount || 0);
    if (!changed) return;
    try { await api.patch(`/students/${s.student_id}/meta`, { promise_date: date || "", promise_amount: Number(amount || 0) }); toast.success("Updated"); onSaved?.(); }
    catch { toast.error("Failed to update"); }
  };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const overdue = date && new Date(date) < today && (s.due || 0) > 0;
  return (
    <div className="flex flex-col gap-1 w-32">
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={save} className={`h-7 text-xs px-1.5 ${overdue ? "border-rose-400 text-rose-600" : ""}`} data-testid={`due-promise-date-${s.admission_number}`} />
      <Input type="number" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} onBlur={save} className="h-7 text-xs px-1.5 tabular" data-testid={`due-promise-amount-${s.admission_number}`} />
      {overdue && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-semibold" data-testid={`due-promise-overdue-${s.admission_number}`}><AlertTriangle size={11} />Overdue</span>}
    </div>
  );
}

export default function DueFees() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [collectStudent, setCollectStudent] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [applied, setApplied] = useState({});
  const [advRows, setAdvRows] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [confirmAssign, setConfirmAssign] = useState(false);
  const [classes, setClasses] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [tags, setTags] = useState([]);

  const loadDue = () => api.get("/due-fees").then((r) => setList(r.data));
  useEffect(() => {
    loadDue();
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/sections").then((r) => setAllSections(r.data.sections || [])).catch(() => {});
    api.get("/settings/tags").then((r) => setTags(r.data.tags || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const adv = Object.keys(applied).length > 0;
    if (!adv) { setAdvRows(null); setSelected(new Set()); return; }
    const params = { limit: 2000 };
    Object.entries(applied).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/students", { params }).then((r) => {
      setAdvRows((r.data.students || []).map((s) => ({
        student_id: s.id, admission_number: s.admission_number, name: s.name, father_name: s.father_name,
        phone: s.phone, class_name: s.class_name, section: s.section,
        total_payable: s.fees_summary?.total_payable || 0, paid: s.fees_summary?.paid || 0,
        discount: s.fees_summary?.discount || 0, due: s.fees_summary?.due || 0,
        promise_date: s.promise_date, promise_amount: s.promise_amount, last_receipt_id: null,
        has_fees: (s.fees_summary?.total_fee || 0) > 0 || (s.fees_summary?.transport_fee || 0) > 0,
      })));
      setSelected(new Set());
    }).catch(() => setAdvRows([]));
  }, [JSON.stringify(applied)]);  // eslint-disable-line
  if (!list) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const openCollect = (studentId = null) => { nav(studentId ? `/fees/collect/${studentId}` : "/fees/collect"); };
  const canCollect = hasPerm(user, "collect_fee");
  const canEditFees = hasPerm(user, "add_discount");

  const filtered = list.filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || s.admission_number.toLowerCase().includes(q.toLowerCase()));
  const advanced = Object.keys(applied).length > 0;
  const rows = advanced ? (advRows || []) : filtered;
  const totalDue = rows.reduce((a, s) => a + (s.due || 0), 0);
  const allChecked = rows.length > 0 && rows.every((s) => selected.has(s.student_id));
  const toggleRow = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((p) => (p.size && rows.every((s) => p.has(s.student_id))) ? new Set() : new Set(rows.map((s) => s.student_id)));
  const setFilter = (key, value) => { setApplied((prev) => { const n = { ...prev }; if (value === "" || value == null || value === "__any__") { delete n[key]; if (key === "class_name") delete n.section; } else n[key] = value; return n; }); };
  const clearFilters = () => setApplied({});
  const sectionsByName = {}; classes.forEach((c) => { const nm = c.name; if (!sectionsByName[nm]) sectionsByName[nm] = []; const sec = (c.section || "").trim(); if (sec && !sectionsByName[nm].includes(sec)) sectionsByName[nm].push(sec); });
  const classNamesList = Array.from(new Set(classes.map((c) => c.name).filter(Boolean)));
  const sectionsForName = (name) => { const s = name ? (sectionsByName[name] || []) : []; return s.length ? s : allSections; };
  const assignFees = async () => {
    if (!selected.size) return;
    setConfirmAssign(false);
    setAssigning(true);
    try {
      const { data } = await api.post("/students/assign-fees", { student_ids: [...selected] });
      let msg = `Assigned fees to ${data.assigned} student(s)`;
      if (data.already) msg += `; ${data.already} already had fees`;
      toast.success(msg);
      const missing = data.no_structure || [];
      setSelected(new Set()); setApplied((a) => ({ ...a })); loadDue();
      if (missing.length) {
        toast.info(`No fee structure for: ${missing.join(", ")} — opening Fee Structure page…`);
        setTimeout(() => nav("/fees"), 1200);
      }
    } catch (e) { toast.error("Assign failed"); }
    finally { setAssigning(false); }
  };

  const remind = async (s, channel) => {
    try {
      const r = await api.post("/notify/fee-reminders", { student_ids: [s.student_id], channel });
      const d = r.data;
      if (d.sent > 0) toast.success(`Reminder sent via ${channel === "whatsapp" ? "WhatsApp" : "SMS"}`);
      else if (d.no_phone > 0) toast.info(`${s.name} has no valid phone number`);
      else if (d.not_configured > 0) toast.info("SMS/WhatsApp gateway not configured — add Twilio keys in backend .env");
      else toast.info("Reminder not sent");
    } catch (e) { toast.error("Failed to send reminder"); }
  };

  const remindAll = async (channel) => {
    const ids = filtered.map((s) => s.student_id);
    if (!ids.length) { toast.error("No due students"); return; }
    setSending(true);
    try {
      const r = await api.post("/notify/fee-reminders", { student_ids: ids, channel });
      const d = r.data;
      if (d.sent > 0) toast.success(`Sent ${d.sent} reminder(s) via ${channel === "whatsapp" ? "WhatsApp" : "SMS"}`);
      else if (d.not_configured > 0) toast.info("SMS/WhatsApp gateway not configured — add Twilio keys in backend .env to enable");
      else toast.info(`No reminders sent (${d.failed} failed, ${d.skipped} without dues)`);
    } catch (e) { toast.error("Bulk reminder failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-3xl font-extrabold text-slate-900">Collect/View Fees</h1><p className="text-slate-500">{rows.length} students · {fmt(totalDue)} outstanding</p></div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowFilter((v) => !v)} data-testid="due-advanced-filter"><Filter size={15} className="mr-1.5" />Advanced Filter</Button>
          {canEditFees && (
            <Button className="bg-amber-600 hover:bg-amber-700" disabled={assigning || selected.size === 0} onClick={() => setConfirmAssign(true)} data-testid="assign-fees-btn"><Sparkles size={15} className="mr-1.5" />Assign Fees{selected.size ? ` (${selected.size})` : ""}</Button>
          )}
          {canCollect && (
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => openCollect(null)} data-testid="collect-fees-top-btn"><HandCoins size={15} className="mr-1.5" />Collect Fees</Button>
          )}
          {hasPerm(user, "view_fees") && (
            <>
              <Button variant="outline" disabled={sending} onClick={() => remindAll("sms")} data-testid="remind-all-sms"><Send size={15} className="mr-1.5" />Remind All (SMS)</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={sending} onClick={() => remindAll("whatsapp")} data-testid="remind-all-wa"><MessageSquare size={15} className="mr-1.5" />Remind All (WhatsApp)</Button>
            </>
          )}
        </div>
      </div>
      <div className="relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-10" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="due-search" />
      </div>
      {showFilter && (
        <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="due-filter-panel">
          <div className="flex flex-wrap items-end gap-2.5">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-0.5">Sort By</label>
              <Select value={applied.sort_by || "__any__"} onValueChange={(v) => setFilter("sort_by", v)}>
                <SelectTrigger className="w-36 h-9" data-testid="due-filter-sort-by"><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent><SelectItem value="__any__">Default</SelectItem>{SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-0.5">Order</label>
              <Select value={applied.sort_dir || "asc"} onValueChange={(v) => setFilter("sort_dir", v)}>
                <SelectTrigger className="w-28 h-9" data-testid="due-filter-sort-dir"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="asc">A → Z</SelectItem><SelectItem value="desc">Z → A</SelectItem></SelectContent>
              </Select>
            </div>
            {FILTER_FIELDS.map((field) => {
              const val = applied[field.key] ?? "";
              return (
                <div key={field.key} className="flex flex-col gap-1" data-testid={`due-filter-chip-${field.key}`}>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-0.5">{field.label}</label>
                  {field.type === "class" ? (
                    <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                      <SelectTrigger className="w-40 h-9" data-testid="due-filter-class-select"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent><SelectItem value="__any__">Any</SelectItem>{classNamesList.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : field.type === "section" ? (
                    <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                      <SelectTrigger className="w-32 h-9" data-testid="due-filter-section-select"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent><SelectItem value="__any__">Any</SelectItem>{sectionsForName(applied.class_name).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : field.type === "select" ? (
                    <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                      <SelectTrigger className="w-36 h-9" data-testid={`due-filter-${field.key}-select`}><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent><SelectItem value="__any__">Any</SelectItem>{(field.key === "tag" ? tags : field.options).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input className="w-36 h-9" value={val} onChange={(e) => setFilter(field.key, e.target.value)} placeholder="Any" data-testid={`due-filter-${field.key}-input`} />
                  )}
                </div>
              );
            })}
            {Object.keys(applied).length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="due-clear-filter" className="h-9 text-rose-500"><X size={14} className="mr-1" />Clear All</Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowFilter(false)} data-testid="due-hide-filter" className="h-9"><X size={14} className="mr-1" />Hide Filters</Button>
          </div>
          {advanced && <p className="text-xs text-slate-400 mt-2">Tip: filter by <b>Without Fees → Yes</b>, tick students, then click <b>Assign Fees</b> to apply each student's class fee structure.</p>}
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        {rows.length === 0 ? <div className="py-16 text-center text-slate-400"><AlertCircle size={40} className="mx-auto mb-2 opacity-40" />{advanced ? "No students match this filter." : "No dues. All clear!"}</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="px-3 py-3">{canEditFees && <input type="checkbox" checked={allChecked} onChange={toggleAll} className="w-4 h-4 accent-indigo-600" data-testid="due-select-all" />}</th><th className="text-left px-4 py-3">Se. No.</th><th className="text-left px-4 py-3">Student</th><th className="text-left px-4 py-3">Class</th><th className="text-right px-4 py-3">Total Fees</th><th className="text-right px-4 py-3">Paid Fees</th><th className="text-right px-4 py-3">Discount</th><th className="text-right px-4 py-3">Due</th><th className="text-left px-4 py-3">Fee Promise</th><th className="text-left px-4 py-3">Last Paid</th><th className="px-4 py-3"></th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((s, idx) => (
                  <tr key={s.student_id} className={`hover:bg-slate-50 ${selected.has(s.student_id) ? "bg-indigo-50/50" : ""}`} data-testid={`due-row-${s.admission_number}`}>
                    <td className="px-3 py-3">{canEditFees && <input type="checkbox" checked={selected.has(s.student_id)} onChange={() => toggleRow(s.student_id)} className="w-4 h-4 accent-indigo-600" data-testid={`due-select-${s.admission_number}`} />}</td>
                    <td className="px-4 py-3 text-slate-500 tabular">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{s.name}<div className="text-xs text-slate-500 mt-0.5">{s.father_name}</div>{s.phone && <div className="text-xs text-slate-500">{s.phone}</div>}</td>
                    <td className="px-4 py-3">{s.class_name}</td>
                    <td className="px-4 py-3 text-right text-slate-700 tabular">{fmt(s.total_payable)}</td>
                    <td className="px-4 py-3 text-right text-emerald-600 font-medium tabular">{fmt(s.paid)}</td>
                    <td className="px-4 py-3 text-right text-amber-600 tabular">{fmt(s.discount)}</td>
                    <td className="px-4 py-3 text-right font-bold text-rose-600 tabular">{fmt(s.due)}</td>
                    <td className="px-4 py-3">{hasPerm(user, "collect_fee") ? <PromiseCell s={s} onSaved={loadDue} /> : (s.promise_date ? <div className="text-xs"><div>{fmtDate(s.promise_date)}</div><div className="text-indigo-600">{fmt(s.promise_amount)}</div></div> : <span className="text-slate-300">—</span>)}</td>
                    <td className="px-4 py-3 text-xs leading-tight">
                      {s.last_receipt_id ? (
                        <div className="space-y-0.5">
                          <div className="text-slate-600">{fmtDate(s.last_paid_date)}</div>
                          <div className="text-emerald-600 font-semibold tabular">{fmt(s.last_paid_amount)}</div>
                          <a href={`${API}/receipts/${s.last_receipt_id}/pdf?token=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline" data-testid={`last-slip-${s.admission_number}`}>
                            <Printer size={12} />{s.last_receipt_number}
                          </a>
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex flex-col items-end gap-1">
                        {canCollect && (
                          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 w-32" onClick={() => openCollect(s.student_id)} data-testid={`collect-fees-${s.admission_number}`}><HandCoins size={14} className="mr-1" />Collect Fees</Button>
                        )}
                        {canEditFees && (
                          <Button size="sm" variant="outline" className="w-32" onClick={() => nav(`/fees/edit/${s.student_id}`)} data-testid={`edit-fees-${s.admission_number}`}><Pencil size={14} className="mr-1" />Edit Fees</Button>
                        )}
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => remind(s, "sms")} title="Send SMS reminder" data-testid={`remind-sms-${s.admission_number}`}><MessageSquare size={14} /></Button>
                          <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => remind(s, "whatsapp")} title="Send WhatsApp reminder" data-testid={`remind-wa-${s.admission_number}`}>WA</Button>
                          <Button size="sm" variant="ghost" onClick={() => nav(`/students/${s.student_id}`)}>View</Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <CollectFeeDialog
        open={collectOpen}
        onOpenChange={setCollectOpen}
        studentId={collectStudent}
        onCollected={loadDue}
      />
      <AlertDialog open={confirmAssign} onOpenChange={setConfirmAssign}>
        <AlertDialogContent data-testid="assign-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Assign fees to {selected.size} student{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>Each selected student will receive their class fee structure. Students that already have fees are skipped.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="assign-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" onClick={assignFees} data-testid="assign-confirm-ok">Assign Fees</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
